import React from 'react'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import WbSunnyIcon from '@material-ui/icons/WbSunny'
import CloudIcon from '@material-ui/icons/Cloud'
import AcUnitIcon from '@material-ui/icons/AcUnit'
import GrainIcon from '@material-ui/icons/Grain'
import OpacityIcon from '@material-ui/icons/Opacity'
import classNames from 'classnames'
import type { StyleClasses } from '@paintscout/ui'
import { Typography } from '@paintscout/ui'

export type ZoomLevel = 'days' | 'weeks' | 'months' | 'quarters'

const useStyles = makeStyles<Theme, TimelineHeaderProps>(
  (theme) => ({
    root: {},
    timelineNavigationHeader: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      borderBottom: '1px solid rgba(226, 232, 240, 0.6)', // slate-200/60
      backgroundColor: '#fff' // bg-white
    },
    container: {
      borderBottom: '1px solid rgba(226, 232, 240, 0.6)', // border-slate-200/60
      backgroundColor: '#fff' // bg-white
    },
    flex: {
      display: 'flex'
    },
    flex1: {
      flex: 1
    },
    header: {
      width: 256,
      flexShrink: 0,
      position: 'sticky',
      left: 0,
      zIndex: 20,
      backgroundColor: '#fff',
      border: '1px solid rgba(226, 232, 240, 0.6)' // border-slate-200/60
    },
    dayBlock: {
      width: 80,
      flexShrink: 0,
      boxSizing: 'border-box',
      border: '1px solid rgba(226, 232, 240, 0.4)', // border-slate-200/40
      padding: 8,
      transition: 'all 0.2s',
      height: 79
    },
    isTodayText: {
      color: '#2563eb'
    },
    isTodayBackgroundColor: {
      backgroundColor: '#eff6ff',
      borderColor: 'rgba(37, 99, 235, 0.2)'
    },
    isTodayWeekendBackgroundColor: {
      backgroundColor: '#fafbfc'
    },
    isHoveredDayText: {
      backgroundColor: '#f8fafc' // slate-50
    },
    weatherTextContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      paddingBottom: theme.spacing(2),
      marginTop: theme.spacing(1) // TODO: Remove this when we add the weather back in
    },
    weatherIconContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      marginTop: theme.spacing(0.25)
    },
    weatherIcon: {
      // className={`w-4 h-4 ${weather.color}`}
      width: theme.spacing(2),
      height: theme.spacing(2)
      // color: '#fbbf24' // yellow-400
    },
    yellow: {
      color: '#fbbf24' // yellow-400
    },
    blue: {
      color: '#3b82f6' // blue-500
    },
    gray: {
      color: '#6b7280' // gray-500
    },
    navigationHeader: {
      width: 256,
      flexShrink: 0,
      borderRight: '1px solid rgba(226, 232, 240, 0.6)', // border-slate-200/60
      background: 'linear-gradient(to bottom, rgba(248,250,252,0.5), #fff)', // from-slate-50/50 to-white
      position: 'sticky',
      left: 0,
      zIndex: 20,
      boxShadow: '2px 0 4px -2px rgba(0,0,0,0.1)'
    },
    navigationSpan: {
      display: 'flex',
      flex: 1,
      background: 'linear-gradient(to bottom, rgba(248,250,252,0.5), #fff)', // from-slate-50/50 to-white
      borderBottom: '1px solid rgba(226, 232, 240, 0.6)', // border-slate-200/60
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: theme.spacing(0.5)
    }
  }),
  { name: 'TimelineHeader' }
)

export interface TimelineHeaderProps {
  classes?: StyleClasses<typeof useStyles>
  startDate: Date
  endDate: Date
  zoomLevel: ZoomLevel
  cellWidth?: number
  hoveredDate?: string | null
  onDateHover?: (date: string | null) => void
}

// Mock weather data generator
const getWeatherForDate = (date: Date) => {
  // Use date as seed for consistent "random" weather
  const seed = date.getDate() + date.getMonth() * 31
  const weatherTypes = [
    { icon: WbSunnyIcon, temp: [65, 75], label: 'Sunny', color: 'yellow' },
    { icon: CloudIcon, temp: [55, 65], label: 'Cloudy', color: 'gray' },
    { icon: GrainIcon, temp: [50, 60], label: 'Rainy', color: 'blue' },
    { icon: OpacityIcon, temp: [58, 68], label: 'Drizzle', color: 'blue' },
    { icon: AcUnitIcon, temp: [30, 40], label: 'Snow', color: 'blue' }
  ]

  const weatherIndex = seed % weatherTypes.length
  const weather = weatherTypes[weatherIndex]
  const temp = weather.temp[0] + (seed % (weather.temp[1] - weather.temp[0]))

  return {
    icon: weather.icon,
    temp,
    label: weather.label,
    color: weather.color
  }
}

function TimelineHeader(props: TimelineHeaderProps) {
  const { startDate, endDate, zoomLevel, cellWidth = 80, hoveredDate, onDateHover } = props
  const classes = useStyles(props)
  const today = new Date(Date.now())
  today.setHours(0, 0, 0, 0)

  const isTodayInPeriod = (periodStart: Date, periodEnd: Date) => {
    return today >= periodStart && today <= periodEnd
  }

  if (zoomLevel === 'days') {
    const days: Date[] = []
    const currentDate = new Date(startDate)

    while (currentDate <= endDate) {
      days.push(new Date(currentDate))
      currentDate.setDate(currentDate.getDate() + 1)
    }

    const formatDay = (date: Date) => {
      return date.toLocaleDateString('en-US', { weekday: 'short' })
    }

    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', { day: 'numeric' })
    }

    const getMonthName = (date: Date) => {
      return date.toLocaleDateString('en-US', { month: 'short' })
    }

    const isWeekend = (date: Date) => {
      const day = date.getDay()
      return day === 0 || day === 6
    }

    // Helper to create a consistent date string in local timezone
    const getLocalDateString = (date: Date) => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    // Group days by month for month headers
    const monthGroups: { month: string; startIndex: number; count: number }[] = []
    let currentMonth = ''
    let monthStartIndex = 0

    days.forEach((date, index) => {
      const month = getMonthName(date)
      if (month !== currentMonth) {
        if (currentMonth) {
          monthGroups.push({ month: currentMonth, startIndex: monthStartIndex, count: index - monthStartIndex })
        }
        currentMonth = month
        monthStartIndex = index
      }
    })

    // Add the last month group
    if (currentMonth) {
      monthGroups.push({ month: currentMonth, startIndex: monthStartIndex, count: days.length - monthStartIndex })
    }

    return (
      <div className={classes.container}>
        {/* Day headers */}
        <div className={classes.flex}>
          <div className={`${classes.flex} ${classes.flex1}`}>
            {days.map((date) => {
              const isToday = date.toDateString() === today.toDateString()
              const weather = getWeatherForDate(date)
              const _WeatherIcon = weather.icon
              const dateStr = getLocalDateString(date)
              const isHovered = hoveredDate === dateStr
              const isWeekendDay = isWeekend(date)
              return (
                <div
                  key={dateStr}
                  data-date={dateStr}
                  onMouseEnter={() => onDateHover?.(dateStr)}
                  onMouseLeave={() => onDateHover?.(null)}
                  style={{ width: `${cellWidth}px` }}
                  className={classNames({
                    [classes.dayBlock]: true,
                    [classes.isTodayBackgroundColor]: isToday,
                    [classes.isTodayWeekendBackgroundColor]: isWeekendDay && !isToday,
                    [classes.isHoveredDayText]: isHovered && !isToday && !isWeekendDay
                  })}
                >
                  <div className={classes.weatherTextContainer}>
                    <Typography variant="subtitle2" className={classNames({ [classes.isTodayText]: isToday })}>
                      {formatDay(date)}
                    </Typography>
                    <div style={{ marginTop: 4 }}>
                      <Typography variant="h4" className={classNames({ [classes.isTodayText]: isToday })}>
                        {formatDate(date)}
                      </Typography>
                    </div>
                    {/* <div className={classes.weatherIconContainer}>
                      <WeatherIcon classes={{ root: `${classes[weather.color]} ${classes.weatherIcon}` }} />
                      <Typography variant="subtitle1">{weather.temp}°F</Typography>
                    </div> */}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  if (zoomLevel === 'weeks') {
    const weeks: Array<{ start: Date; end: Date }> = []
    const currentDate = new Date(startDate)

    while (currentDate <= endDate) {
      const weekStart = new Date(currentDate)
      const weekEnd = new Date(currentDate)
      weekEnd.setDate(weekEnd.getDate() + 6)

      weeks.push({ start: new Date(weekStart), end: new Date(weekEnd) })
      currentDate.setDate(currentDate.getDate() + 7)
    }

    const formatWeekLabel = (start: Date) => {
      return start.toLocaleDateString('en-US', { month: 'short' })
    }

    const formatWeekDate = (start: Date, end: Date) => {
      return `${start.getDate()}-${end.getDate()}`
    }

    return (
      <div className={classes.container}>
        {/* Week headers */}
        <div className={classes.flex}>
          <div className={`${classes.flex} ${classes.flex1}`}>
            {weeks.map((week, index) => {
              const isTodayInWeek = isTodayInPeriod(week.start, week.end)
              return (
                <div
                  key={index}
                  style={{ width: `${cellWidth}px` }}
                  className={classNames({
                    [classes.dayBlock]: true,
                    [classes.isTodayBackgroundColor]: isTodayInWeek
                  })}
                >
                  <div className={classes.weatherTextContainer}>
                    <Typography variant={'subtitle1'} className={classNames({ [classes.isTodayText]: isTodayInWeek })}>
                      {formatWeekLabel(week.start)}
                    </Typography>
                    <Typography
                      variant={'h4'}
                      weight={'normal'}
                      className={classNames({ [classes.isTodayText]: isTodayInWeek })}
                    >
                      {formatWeekDate(week.start, week.end)}
                    </Typography>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  if (zoomLevel === 'months') {
    const months: Date[] = []
    const startMonth = new Date(startDate.getFullYear(), startDate.getMonth(), 1)
    const endMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 1)
    const currentDate = new Date(startMonth)

    while (currentDate <= endMonth) {
      months.push(new Date(currentDate))
      currentDate.setMonth(currentDate.getMonth() + 1, 1)
    }

    return (
      <div className={classes.container}>
        {/* Month headers */}
        <div className={classes.flex}>
          <div className={`${classes.flex} ${classes.flex1}`}>
            {months.map((month, index) => {
              const monthStart = new Date(month.getFullYear(), month.getMonth(), 1)
              const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 0)
              const isTodayInMonth = isTodayInPeriod(monthStart, monthEnd)

              return (
                <div
                  key={index}
                  style={{ width: `${cellWidth}px` }}
                  className={classNames({
                    [classes.dayBlock]: true,
                    [classes.isTodayBackgroundColor]: isTodayInMonth
                  })}
                >
                  <div className={classes.weatherTextContainer}>
                    <div>
                      <Typography
                        variant={'subtitle1'}
                        className={classNames({ [classes.isTodayText]: isTodayInMonth })}
                      >
                        {month.toLocaleDateString('en-US', { month: 'short' })}
                      </Typography>
                    </div>
                    <div>
                      <Typography variant={'body1'} className={classNames({ [classes.isTodayText]: isTodayInMonth })}>
                        {month.getFullYear()}
                      </Typography>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  if (zoomLevel === 'quarters') {
    const quarters: Array<{ year: number; quarter: number; start: Date; end: Date }> = []
    const startQuarterMonth = Math.floor(startDate.getMonth() / 3) * 3
    const endQuarterMonth = Math.floor(endDate.getMonth() / 3) * 3
    const startQuarterDate = new Date(startDate.getFullYear(), startQuarterMonth, 1)
    const endQuarterDate = new Date(endDate.getFullYear(), endQuarterMonth, 1)
    const currentDate = new Date(startQuarterDate)

    while (currentDate <= endQuarterDate) {
      const year = currentDate.getFullYear()
      const quarter = Math.floor(currentDate.getMonth() / 3) + 1
      const quarterStart = new Date(year, (quarter - 1) * 3, 1)
      const quarterEnd = new Date(year, quarter * 3, 0)

      quarters.push({ year, quarter, start: quarterStart, end: quarterEnd })
      currentDate.setMonth(currentDate.getMonth() + 3, 1)
    }

    return (
      <div className={classes.container}>
        {/* Quarter headers */}
        <div className={classes.flex}>
          <div className={`${classes.flex} ${classes.flex1}`}>
            {quarters.map((q, index) => {
              const isTodayInQuarter = isTodayInPeriod(q.start, q.end)

              return (
                <div
                  key={index}
                  style={{ width: `${cellWidth}px` }}
                  className={classNames({
                    [classes.dayBlock]: true,
                    [classes.isTodayBackgroundColor]: isTodayInQuarter
                  })}
                >
                  <div className={classes.weatherTextContainer}>
                    <Typography
                      variant={'subtitle1'}
                      className={classNames({ [classes.isTodayText]: isTodayInQuarter })}
                    >
                      Q{q.quarter}
                    </Typography>
                    <Typography variant={'body1'} className={classNames({ [classes.isTodayText]: isTodayInQuarter })}>
                      {q.year}
                    </Typography>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  // Return null for non-"days" zoom levels to avoid missing return error
  return <></>
}

export default TimelineHeader
